import React, { useState } from 'react';
import SampleAssistantContainer from '../AgentComponent/SampleAssistantContainer';
import assistantSampleData from '../../utils/assistants/sample_assistants_json.json';
import { motion } from 'framer-motion';
import {
  Star,
  ShieldPlus,
  ClipboardList,
  Calendar,
  UserRound,
  ContactRound,
} from 'lucide-react';
import { createAssistant } from '../../utils/api/assistantAPI';
import { fetchAssistants } from '../../store/app/assistantSlice';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';

// Icon map for template icons
const iconMap = {
  'star': <Star size={28} />,
  'shield-plus': <ShieldPlus size={28} />,
  'clipboard-list': <ClipboardList size={28} />,
  'calendar-1': <Calendar size={28} />,
  'user-round': <UserRound size={28} />,
  'contact-round': <ContactRound size={28} />,
};

const AddAgentModal = ({ handleCancel }) => {
  const [agentName, setAgentName] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // "blank" or "sample"
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const userOrgId = useSelector((state) => state?.user?.user?.organizationId);

  // Handle the create assistant logic
  const handleSubmit = async () => {
    const trimmedName = agentName.trim();

    // Validate name
    if (!trimmedName) {
      toast.error('Please enter an assistant name before submitting.');
      return;
    }

    // Validate template selection
    if (!selectedType || (selectedType === 'sample' && selectedIndex === null)) {
      toast.error('Please select a template before creating the assistant.');
      return;
    }

    if (isSubmitting) return; // prevent multiple submissions
    setIsSubmitting(true);

    let payload;

    // Payload for blank template
    if (selectedType === 'blank') {
      payload = {
        name: trimmedName,
        voice: { provider: 'CallHQ', voiceId: 'Priya', speed: 1 },
        transcriber: {
          provider: 'deepgram',
          model: 'nova-3',
          language: 'multi',
        },
        model: {
          provider: 'google',
          model: 'gemini-2.0-flash',
          temperature: 0.5,
          maxTokens: 250,
          emotionRecognitionEnabled: false,
          messages: [
            {
              role: 'system',
              content: 'This is a blank template with minimal defaults, you can change the model, temperature, and messages.',
            },
          ],
        },
        firstMessage: 'Hello.',
        firstMessageMode: 'assistant-speaks-first',
        backgroundSound: 'office',
        silenceTimeoutSeconds: 10,
        backgroundDenoisingEnabled: true,
        firstMessageInterruptionsEnabled: false,
        maxDurationSeconds: 3600,
      };
    } else {
      // Validate assistant index exists
      const assistant = assistantSampleData?.assistants?.[selectedIndex];
      if (!assistant) {
        toast.error('Invalid assistant selected.');
        setIsSubmitting(false);
        return;
      }

      // Payload for sample template
      payload = {
        name: trimmedName,
        voice: {
          provider: 'CallHQ',
          voiceId: assistant.voiceID || 'Neha',
          speed: 1,
        },
        transcriber: {
          provider: 'deepgram',
          model: 'nova-3',
          language: 'multi',
        },
        model: {
          provider: 'google',
          model: 'gemini-2.0-flash',
          temperature: 0.5,
          maxTokens: 250,
          emotionRecognitionEnabled: false,
          messages: [
            {
              role: 'system',
              content: assistant.prompt || 'You are a helpful assistant.',
            },
          ],
        },
        firstMessage: assistant.firstMessage || 'Hi there!',
        firstMessageMode: 'assistant-speaks-first',
        backgroundSound: 'office',
        silenceTimeoutSeconds: 10,
        backgroundDenoisingEnabled: true,
        firstMessageInterruptionsEnabled: false,
        maxDurationSeconds: 3600,
      };
    }

    try {
      await createAssistant(payload);
      dispatch(fetchAssistants(userOrgId));
     
      handleCancel();

      // Reset all state
      setAgentName('');
      setSelectedIndex(null);
      setSelectedType(null);
    } catch (error) {
      console.error('Error creating assistant:', error);
      toast.error('Failed to create assistant: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1050,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflowY: 'auto',
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '24px',
        width: '100%',
        maxWidth: '700px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '12px',
          marginBottom: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '16px',
            fontWeight: 600,
            color: '#374151',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
              style={{ width: '20px', height: '20px' }}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.25a8.999 8.999 0 0115.004 0" />
            </svg>
            Create Assistant
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 'bold' }}>Choose a template</div>
          <p style={{ fontSize: '12px', color: '#6b7280' }}>
            Here's a few templates to get you started, or you can create your own.
          </p>
        </div>

        {/* Name Input */}
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
          Assistant Name{' '}
          <span style={{ fontWeight: 'normal', color: 'blue', fontSize: '12px' }}>
            (This can be changed later.)
          </span>
        </div>
        <input
          type="text"
          placeholder="Enter agent name"
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '20px',
            border: '1px solid #ccc',
            fontSize: '14px',
          }}
        />

        {/* Blank Template */}
        <motion.div
          onClick={() => {
            setSelectedType('blank');
            setSelectedIndex(null);
          }}
          whileHover={{ backgroundColor: '#f9f9f9' }}
          style={{
            border: selectedType === 'blank' ? '2px solid #2B8E55' : '1px dotted gray',
            borderRadius: '12px',
            marginBottom: '20px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          <div>
            <div style={{ fontWeight: '600', fontSize: '16px', color: '#2B8E55' }}>
              Blank Template
            </div>
            <div style={{ color: '#6B7280', fontSize: '14px' }}>
              A minimal template to fully customize.
            </div>
          </div>
        </motion.div>

        {/* Sample Templates */}
        <div className="mb-20 p-10" style={{
          border: '1px solid #ccc',
          borderRadius: '28px',
          position: 'relative',
          backgroundColor: '#fff',
        }}>
          <div style={{
            position: 'absolute',
            top: '-10px',
            left: '16px',
            backgroundColor: '#2B8E55',
            color: '#fff',
            padding: '2px 10px',
            fontSize: '11px',
            borderRadius: '8px',
            fontWeight: 600,
            letterSpacing: '0.5px',
          }}>
            QUICKSTART
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
            marginTop: '15px',
            maxHeight: '40vh',
            overflowY: 'auto',
          }}>
            {assistantSampleData?.assistants?.map((assistant, index) => (
              <SampleAssistantContainer
                key={index}
                title={assistant.title}
                description={assistant.description}
                icon={iconMap[assistant.icon] || <UserRound size={28} />}
                isSelected={selectedType === 'sample' && selectedIndex === index}
                onClick={() => {
                  setSelectedIndex(index);
                  setSelectedType('sample');
                }}
              />
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button onClick={handleCancel} style={{
            padding: '10px 16px',
            backgroundColor: '#e5e7eb',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 500,
            cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={isSubmitting} style={{
            padding: '10px 16px',
            backgroundColor: '#2B8E55',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 500,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.6 : 1,
          }}>
            {isSubmitting ? 'Creating...' : 'Create Assistant'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAgentModal;
